import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

export type VolumeBidLevelColumn = {
  levelId: string
  priceLabel: string
  hourlyVolumeMW: number[]
}

type ReviewBidsTableVolumeProps = Readonly<{
  columns: VolumeBidLevelColumn[]
  volumeDecimalPlaces: number
}>

// Styling constants (matching ReviewBidsTable)
const CELL_PADDING = '8px 12px'
const CELL_PADDING_COMPACT = '6px 12px'
const FONT_SIZE_BODY = '0.8125rem'
const FONT_SIZE_HEADER = '0.75rem'

// Colors
const HEADER_BG = '#f8fafc'
const HEADER_BG_DARK = '#f1f5f9'
const ROW_STRIPE_BG = '#fafbfc'
const STICKY_SHADOW = '2px 0 4px -1px rgba(0, 0, 0, 0.08)'

function ReviewBidsTableVolume({ columns, volumeDecimalPlaces }: ReviewBidsTableVolumeProps) {
  const { t } = useTranslation()

  if (columns.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 200,
          p: 4,
          backgroundColor: '#fafbfc',
        }}
      >
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('bidding.bidding_cascading.review_bids.no_bids')}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        // Scroll shadow indicators
        background: `
          linear-gradient(90deg, white 30%, rgba(255,255,255,0)),
          linear-gradient(90deg, rgba(255,255,255,0), white 70%) 0 100%,
          radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)) 0 100%
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
        backgroundPosition: 'left center, right center, left center, right center',
        backgroundAttachment: 'local, local, scroll, scroll',
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: 'max-content',
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              component="th"
              scope="col"
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 4,
                backgroundColor: HEADER_BG_DARK,
                fontWeight: 600,
                fontSize: FONT_SIZE_HEADER,
                padding: CELL_PADDING,
                verticalAlign: 'bottom',
                borderRight: '1px solid',
                borderRightColor: 'divider',
                borderBottom: '2px solid',
                borderBottomColor: 'primary.main',
                boxShadow: STICKY_SHADOW,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                color: 'text.secondary',
                minWidth: 70,
              }}
            >
              {t('bidding.bidding_cascading.review_bids.hour')}
            </TableCell>
            {columns.map((column, index) => {
              const isLastColumn = index === columns.length - 1

              return (
                <TableCell
                  key={column.levelId}
                  component="th"
                  scope="col"
                  sx={{
                    textAlign: 'right',
                    backgroundColor: HEADER_BG,
                    borderBottom: '2px solid',
                    borderBottomColor: 'primary.main',
                    borderRight: isLastColumn ? 'none' : '1px solid',
                    borderRightColor: 'divider',
                    fontWeight: 600,
                    fontSize: FONT_SIZE_HEADER,
                    padding: CELL_PADDING,
                    color: 'text.primary',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.priceLabel}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.625rem',
                      color: 'text.disabled',
                      ml: 0.5,
                    }}
                  >
                    EUR
                  </Typography>
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {Array.from({ length: 24 }, (_, hourIndex) => {
            const isEvenRow = hourIndex % 2 === 0
            const isLastRow = hourIndex === 23

            return (
              <TableRow
                key={hourIndex}
                sx={{
                  backgroundColor: isEvenRow ? 'transparent' : ROW_STRIPE_BG,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:hover .hour-cell': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <TableCell
                  className="hour-cell"
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: isEvenRow ? 'background.paper' : ROW_STRIPE_BG,
                    fontWeight: 500,
                    fontSize: FONT_SIZE_BODY,
                    fontVariantNumeric: 'tabular-nums',
                    padding: CELL_PADDING_COMPACT,
                    borderRight: '1px solid',
                    borderRightColor: 'divider',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderBottomColor: 'grey.100',
                    boxShadow: STICKY_SHADOW,
                    color: 'text.secondary',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {`${hourIndex.toString().padStart(2, '0')}:00`}
                </TableCell>

                {columns.map((column, colIndex) => {
                  const volumeMW = column.hourlyVolumeMW[hourIndex] ?? 0
                  const isLastColumn = colIndex === columns.length - 1

                  const cellBaseSx = {
                    fontSize: FONT_SIZE_BODY,
                    padding: CELL_PADDING_COMPACT,
                    fontVariantNumeric: 'tabular-nums',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderBottomColor: 'grey.100',
                    borderRight: isLastColumn ? 'none' : '1px solid',
                    borderRightColor: 'divider',
                    transition: 'background-color 0.15s ease',
                  }

                  if (volumeMW === 0) {
                    return (
                      <TableCell
                        key={column.levelId}
                        sx={{
                          ...cellBaseSx,
                          textAlign: 'center',
                          color: 'text.disabled',
                        }}
                      >
                        —
                      </TableCell>
                    )
                  }

                  return (
                    <TableCell
                      key={column.levelId}
                      sx={{
                        ...cellBaseSx,
                        textAlign: 'right',
                        fontWeight: 400,
                      }}
                    >
                      {volumeMW.toFixed(volumeDecimalPlaces)}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ReviewBidsTableVolume
